import { TableRow } from "@carbon6/c6-frontend-design";
import type { TrainingScheduleOperationsItem } from "@/api/trainingSchedule";
import {
  minFreeGpusLabel,
  primaryDatasetLabel,
  recurrenceDetail,
  recurrenceLabel,
  scheduleStatusLabel,
  scheduleTimeLabel,
} from "./scheduleFormat";

/**
 * Operations > Schedule 목록의 한 행.
 *
 * 셀이 표시할 문자열은 여기서 한 번 만든다. 렌더마다 포맷을 다시 계산하지 않고, 열 정의는 만든
 * 필드의 key 만 안다. 링크·툴팁·칩처럼 구조가 필요한 셀은 `item` 원본을 가지고
 * `scheduleColumns` 가 그린다.
 *
 * 행 클릭은 실행 이력 창(`onOpenHistory`)을, 이름 클릭은 Recipe 패널(`onOpenRecipe`)을 연다. 둘 다
 * 누구나 연다. 편집은 이름 칸의 버튼이다.
 *   - `onEdit` 가 null 이면(쓰기 자격 없음) 버튼을 표시하지 않는다.
 *   - `editLocked` 면(명령 진행 중) 버튼만 잠근다.
 */
export type ScheduleRowActivateHandler = (item: TrainingScheduleOperationsItem) => void;

type ScheduleTableRowHandlers = {
  onOpenHistory: ScheduleRowActivateHandler;
  onOpenRecipe: ScheduleRowActivateHandler;
  onEdit: ScheduleRowActivateHandler | null;
  editLocked: boolean;
};

export class ScheduleTableRow extends TableRow {
  readonly item: TrainingScheduleOperationsItem;
  readonly name: string;
  readonly dataset: string;
  // 열 key 로만 쓴다(셀은 recipe 요약을 직접 그린다). c6 열 key 는 행 필드여야 한다.
  readonly config: string;
  readonly branchFollow: string;
  readonly nextRun: string;
  readonly gpuGate: string;
  readonly activeCount: number;
  readonly status: string;
  readonly onOpenRecipe: ScheduleRowActivateHandler;
  readonly onEdit: ScheduleRowActivateHandler | null;
  readonly editLocked: boolean;
  private readonly onOpenHistory: ScheduleRowActivateHandler;

  constructor(
    item: TrainingScheduleOperationsItem,
    { onOpenHistory, onOpenRecipe, onEdit, editLocked }: ScheduleTableRowHandlers,
  ) {
    super(null);
    this.item = item;
    this.onOpenHistory = onOpenHistory;
    this.onOpenRecipe = onOpenRecipe;
    this.onEdit = onEdit;
    this.editLocked = editLocked;
    this.name = item.name;
    this.dataset = primaryDatasetLabel(item.schedule_recipe);
    this.config = item.schedule_recipe.config?.name ?? "—";
    this.branchFollow = item.skip_if_unchanged ? "Yes" : "No";
    this.nextRun = scheduleTimeLabel(item.next_run_at);
    this.gpuGate = minFreeGpusLabel(item.min_free_gpus);
    this.activeCount = item.active_session_count;
    this.status = scheduleStatusLabel(item.status);
  }

  // 반복 축 한 줄. 다음 실행 칸이 주기와 시각을 함께 표시한다.
  get recurrence(): string {
    return recurrenceLabel(this.item);
  }

  // cron 원식과 시간대. 해석 칸의 툴팁이다. interval 이면 null 이다.
  get recurrenceDetail(): string | null {
    return recurrenceDetail(this.item);
  }

  getRowId(): string {
    return this.item.id;
  }

  // status 필터는 source 를 만들기 전에 적용한다(scheduleFilters). 그래서 행은 항상 보인다.
  canShow(): boolean {
    return true;
  }

  canSelect(): boolean {
    return false;
  }

  // 이력은 읽기라 자격을 확인하지 않는다. 어느 행이든 클릭할 수 있다.
  override get isRowClickable(): boolean {
    return true;
  }

  override onRowClick(): void {
    this.onOpenHistory(this.item);
  }
}

type CreateScheduleRowsOptions = {
  // 행 클릭은 실행 이력 창을 연다. 자격과 무관하게 항상 있다.
  onOpenHistory: ScheduleRowActivateHandler;
  // 이름 클릭은 Recipe 패널을 연다. 자격과 무관하게 항상 있다.
  onOpenRecipe: ScheduleRowActivateHandler;
  // 쓰기 자격이 없으면 null 이다. 그러면 어느 행에도 편집 버튼을 표시하지 않는다.
  onEdit: ScheduleRowActivateHandler | null;
  // 명령이 진행 중인 행. 그 행의 편집만 잠근다.
  pendingId: string | null;
};

export function createScheduleRows(
  items: readonly TrainingScheduleOperationsItem[],
  { onOpenHistory, onOpenRecipe, onEdit, pendingId }: CreateScheduleRowsOptions,
): ScheduleTableRow[] {
  return items.map(
    (item) =>
      new ScheduleTableRow(item, {
        onOpenHistory,
        onOpenRecipe,
        onEdit,
        editLocked: pendingId === item.id,
      }),
  );
}
